import { Link, useNavigate } from "react-router-dom";
import { Search, Instagram, MessageCircle, ChevronDown, Heart, Star, ShoppingBag, Truck, Shield, Sparkles, ArrowRight, ChevronLeft, ChevronRight, X, Menu, User, Video, Play } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl, getThumbUrl, getBlurUrl, getSizedUrl } from "../utils/imageUtils";
import SmartImage from "../components/SmartImage";
import { useCustomer } from "../context/CustomerContext";
import Footer from "../components/Footer";
import LazyVideo from "../components/LazyVideo";
import MobileMenu from "../components/MobileMenu";
import FeaturedSlider from "../components/FeaturedSlider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [homepageVideos, setHomepageVideos] = useState([]);
  const videoScrollRef = useRef(null);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [heroImages, setHeroImages] = useState([]);
  const [featuredSlides, setFeaturedSlides] = useState([]);
  const [activeHero, setActiveHero] = useState(0);
  const { customer, addToWishlist: ctxAddToWishlist, removeFromWishlist, isInWishlist } = useCustomer();
  const navigate = useNavigate();

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      const raw = response.data.heroImages || (response.data.heroImage ? [response.data.heroImage] : []);
      const normalized = raw.map(item =>
        typeof item === 'string' ? { url: item, posY: 50, link: '', badgeText: '' } : { url: item.url, posY: item.posY ?? 50, link: item.link || '', badgeText: item.badgeText || '' }
      );
      if (normalized.length > 0) setHeroImages(normalized);
      setHomepageVideos(response.data.homepageVideos || []);
      setFeaturedSlides(response.data.featuredSlides || []);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  // Auto-slide hero images every 5 seconds
  useEffect(() => {
    if (heroImages.length <= 1) return;
    const interval = setInterval(() => {
      setActiveHero(prev => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      // Fallback defaults
      setCategories([
        { id: 'bridal', name: 'Bridal', description: 'Statement blouses for wedding looks', image: '', tag: 'bridal' },
        { id: 'festive', name: 'Festive', description: 'Perfect for celebrations & festivals', image: '', tag: 'festive' },
        { id: 'corset', name: 'Corset', description: 'Trendy corset-style designs', image: '', tag: 'corset' },
        { id: 'everyday', name: 'Everyday', description: 'Elegant daily wear options', image: '', tag: 'everyday' }
      ]);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data.filter(p => p.published));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/catalog?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const addToWishlist = async (productId, productName) => {
    if (!customer) {
      navigate('/login?redirect=/');
      return;
    }
    if (isInWishlist(productId)) {
      await removeFromWishlist(productId);
      toast.success('Removed from wishlist');
    } else {
      await ctxAddToWishlist(productId);
      toast.success('Added to wishlist', { description: productName });
    }
  };

  // Trust badges
  const trustBadges = [
    { icon: Sparkles, title: 'Premium Fabrics Only', description: 'Handpicked quality materials' },
    { icon: Shield, title: 'Designed for Better Fit', description: 'Flattering silhouettes' },
    { icon: Star, title: 'Made for Occasions', description: 'Wedding to casual ready' },
    { icon: Truck, title: 'Easy Exchange Available', description: '7-day hassle-free exchange' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Announcement Bar */}
      <div className="bg-[#1A1A1A] text-white py-2.5 text-center">
        <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-white/60 no-underline">
          PAN India Shipping · Customization Available · Crafted to Order
        </p>
      </div>

      {/* Sticky Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center h-16 md:h-20 relative">
            {/* Left: Mobile Menu / Desktop Nav */}
            <div className="flex items-center gap-6 flex-1">
              <button 
                className="md:hidden p-2 -ml-2"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu size={24} className="text-[#1A1A1A]" />
              </button>
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/catalog" className="text-[#1A1A1A] text-sm font-medium hover:text-[#A85A52] transition-colors relative group">
                  Shop All
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#A85A52] transition-all group-hover:w-full"></span>
                </Link>
                <Link to="/catalog?tag=bridal" className="text-[#1A1A1A] text-sm font-medium hover:text-[#A85A52] transition-colors relative group">
                  Bridal
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#A85A52] transition-all group-hover:w-full"></span>
                </Link>
                <Link to="/catalog?tag=festive" className="text-[#1A1A1A] text-sm font-medium hover:text-[#A85A52] transition-colors relative group">
                  Festive
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#A85A52] transition-all group-hover:w-full"></span>
                </Link>
                <Link to="/catalog?new=true" className="text-[#1A1A1A] text-sm font-medium hover:text-[#A85A52] transition-colors relative group">
                  New Arrivals
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#A85A52] transition-all group-hover:w-full"></span>
                </Link>
              </nav>
            </div>

            {/* Center: Logo */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 z-10">
              <img 
                src="/midha-logo.png" 
                alt="MIDHA" 
                className="h-12 md:h-14 w-auto"
              />
            </Link>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 md:gap-3 flex-1 justify-end">
              <button 
                onClick={() => setShowSearch(!showSearch)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Search size={20} className="text-[#1A1A1A]" />
              </button>
              <Link 
                to={customer ? "/account" : "/login?redirect=/account"} 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors md:hidden" 
                data-testid="header-wishlist-mobile"
              >
                <Heart size={20} className="text-[#1A1A1A]" />
              </Link>
              {customer ? (
                <Link to="/account" className="p-2 hover:bg-gray-100 rounded-full transition-colors hidden md:flex" data-testid="header-wishlist-link">
                  <Heart size={20} className="text-[#1A1A1A]" />
                </Link>
              ) : (
                <Link to="/login" className="p-2 hover:bg-gray-100 rounded-full transition-colors hidden md:flex" data-testid="header-login-link">
                  <User size={20} className="text-[#1A1A1A]" />
                </Link>
              )}
              <Link
                to="/schedule-call"
                className="hidden md:flex items-center gap-2 bg-[#1A1A1A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#3A3632] transition-colors"
                data-testid="header-video-call-btn"
              >
                <Video size={16} />
                Video Call
              </Link>
              <a 
                href="https://wa.me/917021703587"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#20BD5A] transition-colors"
              >
                <MessageCircle size={16} />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Search Bar (Expandable) */}
          {showSearch && (
            <div className="pb-4">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for blouses, colors, occasions..."
                  className="w-full px-4 py-3 pl-12 border border-[#E5E1DA] rounded-full focus:ring-2 focus:ring-[#A85A52] focus:border-transparent font-body"
                  autoFocus
                />
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A39E96]" />
                <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#A85A52] text-white px-4 py-1.5 rounded-full text-sm font-medium hover:bg-[#8C4A42]">
                  Search
                </button>
              </form>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        products={products}
        categories={categories}
        customer={customer}
      />

      {/* Hero Section */}
      <section className="relative bg-[#F9F8F6] overflow-hidden">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-28">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Text Content */}
            <div className="text-center md:text-left order-2 md:order-1 editorial-rise">
              <span className="overline block mb-5">The MIDHA Atelier</span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#1A1A1A] leading-[1.03] tracking-tight mb-6">
                The Art of the<br />Designer Blouse
              </h1>
              <p className="text-lg md:text-xl text-[#68645E] mb-8 font-body font-light leading-relaxed max-w-md mx-auto md:mx-0">
                Crafted for weddings, festive moments, and statement looks — with fit, detail, and quiet luxury in focus.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mb-8">
                <Link to="/catalog" className="btn-primary" data-testid="hero-shop-btn">
                  Explore the Collection
                </Link>
                <Link to="/catalog?bestseller=true" className="btn-secondary">
                  View Bestsellers
                </Link>
              </div>

              {/* Trust Line */}
              <div className="flex items-center justify-center md:justify-start gap-4 text-sm text-[#68645E]">
                <span className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500 fill-yellow-500" />
                  Trusted by 100+ women
                </span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:inline">Premium fabrics</span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:inline">Perfect fit guaranteed</span>
              </div>
            </div>

            {/* Right: Hero Image Slider */}
            <div className="relative order-1 md:order-2">
              <div
                className="relative aspect-[3/4] max-w-md mx-auto overflow-hidden cursor-pointer bg-gradient-to-br from-[#F2EFEB] via-[#F2EFEB] to-[#E5E1DA] editorial-rise"
                onClick={() => {
                  const link = heroImages[activeHero]?.link;
                  if (link) navigate(link);
                }}
                data-testid="hero-slider"
              >
                {heroImages.map((img, i) => {
                  const src = (img.url || img).startsWith('/api/') ? getImageUrl(img.url || img) : (img.url || img);
                  const isActive = activeHero === i;
                  const isAdjacent = Math.abs(activeHero - i) <= 1 || (i === 0 && activeHero === heroImages.length - 1) || (i === heroImages.length - 1 && activeHero === 0);
                  return (
                    <div
                      key={i}
                      className="absolute inset-0 transition-opacity duration-700"
                      style={{ opacity: isActive ? 1 : 0 }}
                    >
                      {(isActive || isAdjacent) && (
                        <SmartImage
                          src={(img.url || img).startsWith('/api/') ? getSizedUrl(img.url || img, 800) : src}
                          blurSrc={getBlurUrl(img.url || img)}
                          alt={`Premium Designer Blouse ${i + 1}`}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: `center ${img.posY ?? 50}%` }}
                          eager={i === 0}
                        />
                      )}
                    </div>
                  );
                })}
                {heroImages.length === 0 && (
                  <div className="w-full h-full animate-pulse bg-gradient-to-br from-[#F2EFEB] to-[#E5E1DA]"></div>
                )}
                {/* Dots */}
                {heroImages.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {heroImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => { e.stopPropagation(); setActiveHero(i); }}
                        className={`h-2 rounded-full transition-all ${activeHero === i ? 'bg-white w-5' : 'bg-white/50 w-2'}`}
                      />
                    ))}
                  </div>
                )}
                {/* Floating Badge */}
                {heroImages[activeHero]?.badgeText && (
                  <div className="absolute -bottom-0 left-4 bg-white rounded-t-2xl shadow-xl p-4 z-10" onClick={(e) => e.stopPropagation()}>
                    <p className="text-sm md:text-base font-medium text-[#1A1A1A] whitespace-pre-line">{heroImages[activeHero].badgeText}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Blouse Slider (below hero) */}
      <FeaturedSlider products={products} slides={featuredSlides} />

      {/* Category Section - Decision Speed */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-3">Shop by Occasion</h2>
            <p className="text-[#68645E] text-lg">Find the perfect blouse for your special moment</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((cat) => (
              <Link 
                key={cat.name}
                to={cat.productIds?.length > 0 ? `/catalog?occasion=${cat.id}` : `/catalog?tag=${cat.tag}`}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              >
                <SmartImage
                  src={cat.image?.startsWith('/api/') ? getThumbUrl(cat.image) : cat.image}
                  blurSrc={getBlurUrl(cat.image)}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  style={{ objectPosition: `center ${cat.posY ?? 50}%` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 text-white">
                  <h3 className="font-heading text-xl md:text-2xl font-light mb-1">{cat.name}</h3>
                  <p className="text-sm text-white/80 mb-3 hidden md:block">{cat.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all">
                    Shop Now <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Building Section - Why MIDHA */}
      <section className="py-24 md:py-32 bg-[#F9F8F6]" data-testid="why-midha-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 bg-[#A85A52]/10 text-[#A85A52] text-sm font-medium rounded-full mb-3 tracking-wide">
              WHY MIDHA
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-3">The MIDHA Promise</h2>
            <p className="text-[#68645E] text-lg max-w-2xl mx-auto">Thoughtfully crafted blouses with the fit, fabric, and finish you deserve — every single time.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {trustBadges.map((badge, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all" data-testid={`why-midha-badge-${index}`}>
                <div className="w-14 h-14 bg-[#A85A52]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <badge.icon size={28} className="text-[#A85A52]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-1">{badge.title}</h3>
                <p className="text-sm text-[#68645E]">{badge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bestsellers Section - Critical for Conversion */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-2">Most Loved Blouses</h2>
              <p className="text-[#68645E]">Our customers' top picks</p>
            </div>
            <Link 
              to="/catalog?bestseller=true" 
              className="hidden md:inline-flex items-center gap-2 text-[#A85A52] font-medium hover:gap-3 transition-all"
            >
              View All <ArrowRight size={18} />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#A85A52]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.slice(0, 8).map((product, index) => (
                <div key={product.id} className="group relative" data-testid={`product-card-${product.id}`}>
                  {/* Editorial: no promotional badges */}

                  {/* Wishlist Button */}
                  <button 
                    onClick={(e) => { e.preventDefault(); addToWishlist(product.id, product.name); }}
                    className="absolute top-3 right-3 z-10 p-2 bg-white/90 rounded-full shadow hover:bg-white transition-colors"
                  >
                    <Heart 
                      size={18} 
                      className={isInWishlist(product.id) ? 'text-red-500 fill-red-500' : 'text-[#68645E]'} 
                    />
                  </button>

                  {/* Product Image */}
                  <Link to={`/product/${product.slug || product.id}`}>
                    <div className="aspect-[3/4] bg-[#F2EFEB] overflow-hidden">
                      <SmartImage
                        src={getThumbUrl(product.images?.[0]) || 'https://via.placeholder.com/300x400'}
                        blurSrc={getBlurUrl(product.images?.[0])}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      />
                    </div>
                  </Link>

                  {/* Product Info */}
                  <div className="pt-4">
                    <Link to={`/product/${product.slug || product.id}`}>
                      <h3 className="font-body text-sm text-[#1A1A1A] tracking-wide mb-1.5 line-clamp-1 transition-colors group-hover:text-[#A85A52]">
                        {product.name}
                      </h3>
                    </Link>
                    <div className="flex items-baseline gap-2">
                      <span className="font-heading text-lg text-[#1A1A1A]">₹{product.price?.toLocaleString() || '1,999'}</span>
                      {product.originalPrice && (
                        <span className="text-xs text-[#A39E96] line-through">₹{product.originalPrice.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile View All */}
          <div className="mt-8 text-center md:hidden">
            <Link 
              to="/catalog" 
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-6 py-3 rounded-full font-medium"
            >
              View All Products <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Homepage Videos Section - Single horizontal scrolling row */}
      {homepageVideos.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F9F8F6]" data-testid="homepage-videos-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-3">Our Collection in Motion</h2>
              <p className="text-[#68645E]">Watch our blouses come alive</p>
            </div>
            <div className="relative">
              {/* Left Arrow */}
              {homepageVideos.length > 1 && (
                <button
                  onClick={() => { if (videoScrollRef.current) videoScrollRef.current.scrollBy({ left: -280, behavior: 'smooth' }); }}
                  className="hidden md:flex absolute -left-3 md:left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center hover:bg-gray-50 transition-colors"
                  data-testid="video-slide-left"
                >
                  <ChevronLeft size={20} className="text-[#1A1A1A]" />
                </button>
              )}
              {/* Scrollable Container - single row, 4 visible at a time, slide for rest */}
              <div
                ref={videoScrollRef}
                className="flex flex-nowrap gap-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-1 pb-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {homepageVideos.map((video) => {
                  const productLink = video.productLink || (video.productId ? `/product/${video.productId}` : '');
                  return (
                  <div
                    key={video.id || video.url}
                    onClick={() => { if (productLink) navigate(productLink); }}
                    className={`flex-none w-[78%] sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)] snap-start group relative rounded-2xl overflow-hidden bg-black shadow-lg ${productLink ? 'cursor-pointer' : ''}`}
                    data-testid={`homepage-video-${video.id}`}
                  >
                    <div className="aspect-[9/16]">
                      <LazyVideo
                        src={video.url.startsWith('/api') ? `${BACKEND_URL}${video.url}` : video.url}
                        poster={video.poster ? (video.poster.startsWith('/api') ? `${BACKEND_URL}${video.poster}` : video.poster) : undefined}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {(video.title || productLink) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-4 pointer-events-none">
                        {video.title && <h3 className="text-white font-semibold text-sm">{video.title}</h3>}
                        {video.description && <p className="text-white/70 text-xs mt-1 line-clamp-2">{video.description}</p>}
                        {productLink && (
                          <span className="inline-flex items-center gap-1 mt-2 text-white text-xs font-semibold bg-[#A85A52] px-2.5 py-1 rounded-full group-hover:bg-[#8C4A42] transition-colors">
                            View Product <ArrowRight size={12} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              {/* Right Arrow */}
              {homepageVideos.length > 1 && (
                <button
                  onClick={() => { if (videoScrollRef.current) videoScrollRef.current.scrollBy({ left: 280, behavior: 'smooth' }); }}
                  className="hidden md:flex absolute -right-3 md:right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg rounded-full items-center justify-center hover:bg-gray-50 transition-colors"
                  data-testid="video-slide-right"
                >
                  <ChevronRight size={20} className="text-[#1A1A1A]" />
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Urgency strip removed for editorial calm */}

      {/* Instagram / Social CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-[#1A1A1A] mb-4">Follow Us on Instagram</h2>
          <p className="text-[#68645E] mb-6">@midha_thedesignerblouse</p>
          <a 
            href="https://www.instagram.com/midha_thedesignerblouse"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-opacity"
          >
            <Instagram size={20} />
            Follow @midha_thedesignerblouse
          </a>
        </div>
      </section>

      {/* Floating Action Buttons */}
      <Link
        to="/schedule-call"
        className="fixed bottom-24 right-6 bg-[#1A1A1A] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all z-40 group"
        data-testid="video-call-float-button"
      >
        <Video size={28} className="group-hover:rotate-12 transition-transform" />
      </Link>
      <a
        href="https://wa.me/917021703587?text=Hi%2C%20I'm%20interested%20in%20your%20designer%20blouses"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all z-40 group"
        data-testid="whatsapp-float-button"
      >
        <MessageCircle size={28} className="group-hover:rotate-12 transition-transform" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
